import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, FileText, Truck, Receipt, Plus, Trash2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EstimateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Conversion modal
  const [convertType, setConvertType] = useState(null); // 'proforma' | 'challan' | 'sale'
  const [convertItems, setConvertItems] = useState([]);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const init = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/estimates/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
      } catch (err) { setError('Failed to load estimate'); }
      finally { setLoading(false); }
    };
    init();
  }, [id]);

  const getName = (list, itemId, field = 'name') => list.find(i => i.id === itemId)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const productMap = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  // Conversion logic
  const openConvertModal = (type) => {
    const items = (doc.line_items || []).map(li => ({
      product_id: li.product_id || '',
      description: li.description || '',
      mrp: li.mrp || '',
      quantity: li.quantity || 1,
      unit_id: li.unit_id || '',
      price_per_unit: li.price_per_unit || 0,
      discount_percentage: li.discount_percentage || 0,
      discount_amount: li.discount_amount || 0,
      tax_id: li.tax_id || '',
      tax_amount: li.tax_amount || 0,
      total_amount: li.total_amount || 0,
    }));
    setConvertItems(items.length ? items : [emptyItem()]);
    setConvertError('');
    setConvertType(type);
  };

  const emptyItem = () => ({
    product_id: '', description: '', mrp: '', quantity: 1, unit_id: '',
    price_per_unit: 0, discount_percentage: 0, discount_amount: 0,
    tax_id: '', tax_amount: 0, total_amount: 0,
  });

  const recalcItem = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const ppu = parseFloat(item.price_per_unit) || 0;
    const discPct = parseFloat(item.discount_percentage) || 0;
    const base = qty * ppu;
    const discAmt = base * (discPct / 100);
    const afterDisc = base - discAmt;
    const tax = taxes.find(t => t.id === item.tax_id);
    const taxAmt = tax ? afterDisc * (tax.percentage / 100) : 0;
    return { ...item, discount_amount: Math.round(discAmt * 100) / 100, tax_amount: Math.round(taxAmt * 100) / 100, total_amount: Math.round((afterDisc + taxAmt) * 100) / 100 };
  };

  const updateConvertItem = (idx, field, value) => {
    setConvertItems(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], [field]: value };
      if (['quantity', 'price_per_unit', 'discount_percentage', 'tax_id'].includes(field)) {
        n[idx] = recalcItem(n[idx]);
      }
      if (field === 'product_id') {
        const prod = productMap[value];
        if (prod) {
          n[idx].unit_id = prod.unit_id || '';
          n[idx].mrp = prod.sale_rate || '';
          n[idx].price_per_unit = prod.sale_rate || 0;
          n[idx] = recalcItem(n[idx]);
        }
      }
      return n;
    });
  };

  const convertTotals = useMemo(() => {
    let subtotal = 0;
    convertItems.forEach(i => { subtotal += (parseFloat(i.total_amount) || 0); });
    const total = Math.round(subtotal);
    const roundOffAmt = Math.round((total - subtotal) * 100) / 100;
    return { subtotal: Math.round(subtotal * 100) / 100, total, roundOffAmt };
  }, [convertItems]);

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    setConvertError('');
    setConvertSubmitting(true);
    try {
      const lineItems = convertItems.map(li => ({
        product_id: li.product_id || null,
        description: li.description || null,
        mrp: parseFloat(li.mrp) || null,
        quantity: parseFloat(li.quantity) || 1,
        unit_id: li.unit_id || null,
        price_per_unit: parseFloat(li.price_per_unit) || 0,
        discount_percentage: parseFloat(li.discount_percentage) || 0,
        discount_amount: parseFloat(li.discount_amount) || 0,
        tax_id: li.tax_id || null,
        tax_amount: parseFloat(li.tax_amount) || 0,
        total_amount: parseFloat(li.total_amount) || 0,
      }));

      const today = new Date().toISOString().slice(0, 10);
      let url, body, redirectPath;

      if (convertType === 'proforma') {
        url = `${BACKEND_URL}/api/proforma-invoices`;
        body = {
          customer_id: doc.customer_id || null,
          company_id: doc.company_id || null,
          invoice_date: today,
          state_of_supply: doc.state_of_supply || null,
          line_items: lineItems,
          description: doc.description || null,
          round_off: true,
          round_off_amount: convertTotals.roundOffAmt,
          subtotal: convertTotals.subtotal,
          total: convertTotals.total,
        };
        redirectPath = '/admin/sale/proforma-invoices';
      } else if (convertType === 'challan') {
        url = `${BACKEND_URL}/api/delivery-challans`;
        body = {
          customer_id: doc.customer_id || null,
          company_id: doc.company_id || null,
          challan_date: today,
          state_of_supply: doc.state_of_supply || null,
          line_items: lineItems,
          description: doc.description || null,
          round_off: true,
          round_off_amount: convertTotals.roundOffAmt,
          subtotal: convertTotals.subtotal,
          total: convertTotals.total,
        };
        redirectPath = '/admin/sale/delivery-challans';
      } else if (convertType === 'sale') {
        url = `${BACKEND_URL}/api/sale-invoices`;
        body = {
          sale_type: 'Cash',
          customer_id: doc.customer_id || null,
          company_id: doc.company_id || null,
          invoice_date: today,
          state_of_supply: doc.state_of_supply || null,
          line_items: lineItems,
          payments: [{ payment_type: 'Cash', amount: 0 }],
          description: doc.description || null,
          round_off: true,
          round_off_amount: convertTotals.roundOffAmt,
          subtotal: convertTotals.subtotal,
          total: convertTotals.total,
          received_amount: 0,
          balance: convertTotals.total,
        };
        redirectPath = '/admin/sale/invoices';
      }

      await axios.post(url, body, getAuthHeaders());
      navigate(redirectPath);
    } catch (err) {
      setConvertError(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertSubmitting(false);
    }
  };

  const convertLabel = { proforma: 'Proforma Invoice', challan: 'Delivery Challan', sale: 'Sale Invoice' };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  return (
    <div data-testid="estimate-detail-page">
      <button onClick={() => navigate('/admin/sale/estimates')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Estimates
      </button>

      {/* Header + Convert Buttons */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Estimate / Quotation Details</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => openConvertModal('proforma')} data-testid="convert-proforma-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-blue-700 transition-colors">
            <FileText className="h-3.5 w-3.5" /> Convert to Proforma
          </button>
          <button onClick={() => openConvertModal('challan')} data-testid="convert-challan-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-purple-700 transition-colors">
            <Truck className="h-3.5 w-3.5" /> Convert to Challan
          </button>
          <button onClick={() => openConvertModal('sale')} data-testid="convert-sale-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-green-700 transition-colors">
            <Receipt className="h-3.5 w-3.5" /> Convert to Sale Bill
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Quotation Number</span><span className="text-sm font-semibold text-slate-900">{doc.ref_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Date</span><span className="text-sm text-slate-900">{formatDate(doc.estimate_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Customer</span><span className="text-sm text-slate-900">{doc.customer_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      {/* Line Items Table */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* ========== CONVERSION MODAL ========== */}
      {convertType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-5xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                Convert to {convertLabel[convertType]}
              </h2>
              <button onClick={() => setConvertType(null)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit} className="p-6">
              {convertError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5" /><span>{convertError}</span>
                </div>
              )}

              {/* Source info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                <div><span className="text-xs text-slate-500 block">Customer</span><span className="font-medium text-slate-900">{doc.customer_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Company</span><span className="font-medium text-slate-900">{doc.company_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">State of Supply</span><span className="font-medium text-slate-900">{doc.state_of_supply || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Source Estimate</span><span className="font-medium text-slate-900">{doc.ref_number || '-'}</span></div>
              </div>

              {/* Editable Line Items */}
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-2 py-2 text-left w-8">#</th>
                      <th className="px-2 py-2 text-left min-w-[180px]">Product</th>
                      <th className="px-2 py-2 text-left min-w-[120px]">Description</th>
                      <th className="px-2 py-2 text-right w-20">Qty</th>
                      <th className="px-2 py-2 text-left w-24">Unit</th>
                      <th className="px-2 py-2 text-right w-24">Price/Unit</th>
                      <th className="px-2 py-2 text-right w-16">Disc %</th>
                      <th className="px-2 py-2 text-left w-32">Tax</th>
                      <th className="px-2 py-2 text-right w-24">Amount</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {convertItems.map((li, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-slate-400">{idx + 1}</td>
                        <td className="px-2 py-2">
                          <select value={li.product_id} onChange={e => updateConvertItem(idx, 'product_id', e.target.value)}
                            data-testid={`conv-product-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">-- Select --</option>
                            {products.filter(p => p.active).map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input type="text" value={li.description} onChange={e => updateConvertItem(idx, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" placeholder="Desc" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.quantity} onChange={e => updateConvertItem(idx, 'quantity', e.target.value)}
                            min="0" step="0.01" data-testid={`conv-qty-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <select value={li.unit_id} onChange={e => updateConvertItem(idx, 'unit_id', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">Unit</option>
                            {units.filter(u => u.active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.price_per_unit} onChange={e => updateConvertItem(idx, 'price_per_unit', e.target.value)}
                            min="0" step="0.01" data-testid={`conv-price-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.discount_percentage} onChange={e => updateConvertItem(idx, 'discount_percentage', e.target.value)}
                            min="0" max="100" step="0.01"
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2">
                          <select value={li.tax_id} onChange={e => updateConvertItem(idx, 'tax_id', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">No Tax</option>
                            {taxes.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2 text-right font-medium text-slate-900" data-testid={`conv-total-${idx}`}>
                          {(parseFloat(li.total_amount) || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-2">
                          {convertItems.length > 1 && (
                            <button type="button" onClick={() => setConvertItems(p => p.filter((_, i) => i !== idx))}
                              className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button type="button" onClick={() => setConvertItems(p => [...p, emptyItem()])} data-testid="conv-add-item"
                className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 text-sm font-medium mb-4">
                <Plus className="h-4 w-4" /> Add Item
              </button>

              {/* Summary */}
              <div className="border-t border-slate-200 pt-4 mb-4">
                <div className="max-w-xs ml-auto space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{convertTotals.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span>{convertTotals.roundOffAmt >= 0 ? '+' : ''}{convertTotals.roundOffAmt.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{convertTotals.total.toLocaleString()}</span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setConvertType(null)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={convertSubmitting} data-testid="conv-submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-none hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create {convertLabel[convertType]}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
